// client/src/components/pages/LocationAnalytics/index.tsx (converted from .js, ST-00229)
//
// Location Analytics: drill column by column from a perspective's locations into
// products and trading partners, down to the tickets. ST-00229: the period opens
// on the previous month on the 1st (ANL-19), and a column whose list the server
// capped says "Top N of M" (ANL-20).
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PageHeader } from '../../../types/commonComponents';
import {
  FaMapMarkerAlt,
  FaSync,
  FaListUl,
  FaLayerGroup,
  FaHome,
  FaChevronRight,
} from 'react-icons/fa';
import api from '../../../services/api';
import DateSelector from './DateSelector';
import TypeSelector from './TypeSelector';
import DrillColumn from './DrillColumn';
import SelectionBar from './SelectionBar';
import TicketsModal from './TicketsModal';
import { defaultDrillRange } from './defaultRange';
import type {
  DrillDimension,
  DrillDimensionItem,
  DrillResponse,
  DrillScope,
  LocationAnalyticsType,
} from '../../../types/clientPages/locationAnalytics';

const DIM_ORDER: DrillDimension[] = ['location', 'partner', 'product'];

/** One committed drill step: a column's dimension and the value clicked in it. */
interface Pin {
  dim: DrillDimension;
  value: string;
}

/** Rows checked in "Select to sum" mode (one column / dimension at a time). */
interface Selection {
  colIndex: number;
  dim: DrillDimension;
  values: Set<string>;
}

const scopeKey = (s: DrillScope): string =>
  JSON.stringify([
    s.type,
    s.startDate,
    s.endDate,
    s.location || '',
    s.product || '',
    s.partner || '',
  ]);

const LocationAnalytics = () => {
  // Filters. The opening period is computed once (it reads the clock).
  const [initialRange] = useState(defaultDrillRange);
  const [startDate, setStartDate] = useState<string>(initialRange.startDate);
  const [endDate, setEndDate] = useState<string>(initialRange.endDate);
  const [type, setType] = useState<LocationAnalyticsType>('Dispatch');

  // Drill chain: committed pins (one per column to the left of the frontier).
  const [pins, setPins] = useState<Pin[]>([]);
  const [preferredDim, setPreferredDim] = useState<DrillDimension | null>(null);

  // One /drill result per column scope (a prefix of pins), cached by scope key.
  const [colData, setColData] = useState<DrillResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef(new Map<string, DrillResponse>());

  const [selectMode, setSelectMode] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [showTickets, setShowTickets] = useState(false);

  const partnerLabel = type === 'Dispatch' ? 'Receipt Locations' : 'Dispatch Locations';
  const DIM_META = useMemo<Record<DrillDimension, { tab: string; label: string }>>(
    () => ({
      location: { tab: 'Locations', label: `${type} Locations` },
      partner: { tab: 'Partners', label: partnerLabel },
      product: { tab: 'Products', label: 'Products' },
    }),
    [type, partnerLabel]
  );

  const scopeForIndex = useCallback(
    (i: number): DrillScope => {
      const s: DrillScope = { type, startDate, endDate };
      for (let j = 0; j < i; j++) {
        const pin = pins[j];
        if (pin) s[pin.dim] = pin.value;
      }
      return s;
    },
    [type, startDate, endDate, pins]
  );

  // Fetch /drill for every column scope (root … frontier), reusing cache so going
  // deeper only fetches the new frontier column.
  useEffect(() => {
    let cancelled = false;
    const scopes: DrillScope[] = [];
    for (let i = 0; i <= pins.length; i++) scopes.push(scopeForIndex(i));

    setLoading(true);
    setError(null);
    Promise.all(
      scopes.map((s) => {
        const key = scopeKey(s);
        const cached = cacheRef.current.get(key);
        if (cached) return Promise.resolve(cached);
        return api.locationAnalytics.getDrill({ ...s }).then((r: { data: DrillResponse }) => {
          cacheRef.current.set(key, r.data);
          return r.data;
        });
      })
    )
      .then((results) => {
        if (cancelled) return;
        setColData(results);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Error fetching drill data:', err);
        setError('Could not load location data. Check your connection and try Refresh.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pins, scopeForIndex]);

  // Still-drillable dimensions for a column, in display order (partner hidden at root).
  const availableDims = useCallback(
    (colIndex: number, data: DrillResponse | undefined): DrillDimension[] => {
      const dims = data?.dimensions || {};
      return DIM_ORDER.filter((d) => {
        if (colIndex === 0 && d === 'partner') return false;
        const list = dims[d];
        return Array.isArray(list) && list.length > 0;
      });
    },
    []
  );

  // --- Drill interactions -------------------------------------------------
  const handleTypeChange = (newType: LocationAnalyticsType) => {
    if (newType === type) return;
    setType(newType);
    setPins([]);
    setPreferredDim(null);
    setSelection(null);
  };

  const handleDimChange = (colIndex: number, dim: DrillDimension) => {
    if (colIndex < pins.length) setPins(pins.slice(0, colIndex));
    setPreferredDim(dim);
    setSelection(null);
  };

  const handleDrill = (colIndex: number, dim: DrillDimension, value: string) => {
    const next = pins.slice(0, colIndex);
    next.push({ dim, value });
    setPins(next);
    setPreferredDim(null);
    setSelection(null);
  };

  const handleReset = () => {
    setPins([]);
    setPreferredDim(null);
    setSelection(null);
  };

  const handleToggleSelect = (colIndex: number, dim: DrillDimension, value: string) => {
    setSelection((prev) => {
      if (!prev || prev.colIndex !== colIndex) return { colIndex, dim, values: new Set([value]) };
      const values = new Set(prev.values);
      if (values.has(value)) values.delete(value);
      else values.add(value);
      if (values.size === 0) return null;
      return { ...prev, values };
    });
  };

  const toggleSelectMode = () => {
    setSelectMode((m) => !m);
    setSelection(null);
  };

  const handleRefresh = () => {
    cacheRef.current.clear();
    setPins((p) => [...p]);
  };

  const selectionSummary = useMemo(() => {
    if (!selection) return null;
    const data = colData[selection.colIndex];
    const list: DrillDimensionItem[] = data?.dimensions?.[selection.dim] || [];
    const items = list.filter((it) => selection.values.has(it.value));
    if (items.length === 0) return null;
    return { dimLabel: DIM_META[selection.dim]?.label || selection.dim, items };
  }, [selection, colData, DIM_META]);

  const frontierData = colData[pins.length];
  const frontierScope = useMemo(() => scopeForIndex(pins.length), [scopeForIndex, pins.length]);
  const kpis = frontierData?.kpis || null;
  const hasData = !!kpis && kpis.totalTickets > 0;
  const columnCount = pins.length + 1;

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <PageHeader
        icon={FaMapMarkerAlt}
        title="Location Analytics"
        subtitle="Drill column by column — products and trading partners branch off, down to individual tickets"
        sticky={false}
        className="flex-shrink-0"
      />

      <div className="flex-1 min-h-0 w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-4 overflow-hidden">
        {/* Controls */}
        <div className="bg-surface p-4 rounded-lg border border-gray-200 shadow-sm flex-shrink-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DateSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </div>
            <div>
              <TypeSelector selectedType={type} setSelectedType={handleTypeChange} />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between flex-shrink-0">
            <p className="text-red-600">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Drill toolbar — borderless strip of actions for the columns below */}
        {hasData && (
          <div className="flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
            <div className="flex items-center min-h-[2.25rem]">
              {pins.length > 0 && (
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FaHome className="text-xs" /> Reset drill
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSelectMode}
                aria-pressed={selectMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  selectMode
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-surface border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FaLayerGroup className="text-xs" />
                {selectMode ? 'Selecting — click rows to sum' : 'Select to sum'}
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                aria-label="Refresh data"
                title="Refresh data"
                className="flex items-center justify-center w-9 h-9 text-gray-500 bg-surface border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors disabled:opacity-50"
              >
                <FaSync className={`text-sm ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowTickets(true)}
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors shadow-sm"
              >
                <FaListUl className="text-xs" />
                View {kpis.totalTickets.toLocaleString()} tickets
              </button>
            </div>
          </div>
        )}

        {/* The branch: one unified panel, columns as seamless divided sections */}
        {loading && colData.length === 0 ? (
          <div className="bg-surface rounded-lg border border-gray-200 shadow-sm flex-1 min-h-0 flex flex-col items-center justify-center text-center text-gray-400">
            <FaSync className="animate-spin text-2xl mb-2" />
            Loading…
          </div>
        ) : !hasData && !loading ? (
          <div className="bg-surface rounded-lg border border-gray-200 shadow-sm flex-1 min-h-0 flex flex-col items-center justify-center text-center">
            <FaMapMarkerAlt className="text-4xl text-gray-300 mb-4" />
            <p className="text-gray-500">No data for the selected filters.</p>
            <p className="text-sm text-gray-400 mt-1">
              Try widening the date range or switching perspective.
            </p>
          </div>
        ) : (
          <div className="bg-surface rounded-lg border border-gray-200 shadow-sm flex-1 min-h-0 overflow-x-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
            <div className="flex items-stretch divide-x divide-gray-200 h-full">
              {Array.from({ length: columnCount }).map((_, i) => {
                const data = colData[i];
                const isFrontier = i === pins.length;

                if (!data) {
                  return (
                    <div
                      key={i}
                      className="w-80 flex-shrink-0 h-full flex items-center justify-center text-gray-300"
                    >
                      <FaSync className="animate-spin text-xl" />
                    </div>
                  );
                }

                const dimKeys = availableDims(i, data);
                const pin = pins[i];
                if (dimKeys.length === 0 || (!isFrontier && !pin)) {
                  return (
                    <div
                      key={i}
                      className="w-80 flex-shrink-0 h-full flex flex-col items-center justify-center p-6 text-center"
                    >
                      <FaChevronRight className="text-2xl text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">End of the branch.</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Use “View tickets” above to see the underlying records.
                      </p>
                    </div>
                  );
                }

                const activeDim: DrillDimension =
                  isFrontier || !pin
                    ? preferredDim && dimKeys.includes(preferredDim)
                      ? preferredDim
                      : (dimKeys[0] as DrillDimension)
                    : pin.dim;
                const items = data.dimensions[activeDim] || [];
                const selectedValue = isFrontier || !pin ? null : pin.value;
                const dims = dimKeys.map((k) => ({ key: k, tab: DIM_META[k].tab }));
                const selectedSet = selection && selection.colIndex === i ? selection.values : null;

                return (
                  <DrillColumn
                    key={i}
                    dims={dims}
                    activeDim={activeDim}
                    onDimChange={(dim) => handleDimChange(i, dim)}
                    items={items}
                    total={data.dimensionTotals?.[activeDim]}
                    selectedValue={selectedValue}
                    onDrill={(value) => handleDrill(i, activeDim, value)}
                    selectMode={selectMode}
                    selectedSet={selectedSet}
                    onToggleSelect={(value) => handleToggleSelect(i, activeDim, value)}
                    isFrontier={isFrontier}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      <SelectionBar selection={selectionSummary} onClear={() => setSelection(null)} />

      {showTickets && <TicketsModal scope={frontierScope} onClose={() => setShowTickets(false)} />}
    </div>
  );
};

export default LocationAnalytics;
